/**
 * Asset discovery over a virtual file tree (no real filesystem access).
 *
 * `discoverAssetsInTreeEntries()` is the server-side counterpart to
 * `discoverAssetsInTree()` — same kind resolution, manifest parsing,
 * publisher/name derivation, requires extraction, and SHA256 hashing — but
 * driven by a flat list of {@link TreeEntry} (the shape returned by GitHub's
 * Git Trees API: `GET /repos/:owner/:repo/git/trees/:tree_sha?recursive=1`).
 *
 * Used by the public Catalog backend (`@postxl/skaile-store-api`) so that
 * Source ingestion never touches the local filesystem and never runs
 * `git clone` (per spec § Phase 2: "no client-side cloning, ever").
 *
 * The function is async because manifest parsing and content hashing both
 * require fetching blob bytes from the remote host (e.g. GitHub Blobs API).
 *
 * Per-kind file-filter logic is mirrored from `builtin-providers.ts` — the
 * provider implementations there read `fs.readdirSync` / `fs.existsSync`,
 * which doesn't translate to a virtual tree, so this module reimplements
 * the same patterns against the supplied {@link TreeEntry} list. The
 * resulting `fileFilter.files` set is identical to what
 * `discoverAssetsInTree()` would produce for the same content laid out on
 * disk.
 *
 * @module
 */

import * as crypto from "node:crypto";
import * as path from "node:path";
import { parseFrontmatter } from "@skaile/workspaces/core/manifest";
import { isValidAssetName } from "@skaile/workspaces/core/models";
import { SKAILE_MANIFEST_FILENAME } from "@skaile/workspaces/core/publish-manifest";
import type { AssetKindFileFilter, IAssetKindRegistry } from "@skaile/workspaces/plugins";
import * as yaml from "js-yaml";
import { AGENT_COMPANION_DIRS, SKILL_COMPANION_DIRS } from "./atomization-conventions.js";
import type { AtomizationPolicy, DiscoveredAsset, DiscoveryResult } from "./discover.js";
import { createDefaultRegistry, DEFAULT_ATOMIZATION_POLICY } from "./discover.js";
import {
  type AssetFs,
  buildSkillNameToRefs,
  closeOverReferences,
  lintUncoveredReferences,
  pushBodyLinkEdge,
  type ReferenceWarning,
  resolveSlashCommand,
  scanAssetReferences,
  scanSlashCommandNames,
} from "./reference-lint.js";
import { buildRequiresGraph, type RequiresEdge } from "./requires-graph.js";
import type { AssetEntry, SourceConfig, SourceConfigV2 } from "./source-config.js";
import {
  barePublisher,
  decodeManifestSourceConfig,
  isExcludedDevPath,
  isManifestMode,
  mergeSourceConfigs,
} from "./source-config.js";
import { applyDomains, isDomainMdPath, parseDomainMd, type DomainMd } from "./domains.js";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * A single entry in a virtual file tree (the shape returned by GitHub's
 * Git Trees API).
 *
 * Only the four fields used by discovery are required; consumers may pass
 * Octokit's full `GitTreeResponse[]` directly because TypeScript structural
 * typing accepts the superset.
 *
 * @docLink packages/discovery/concepts#tree-entry
 */
export interface TreeEntry {
  /** Repo-relative path (forward slashes), e.g. `domain/skills/review/SKILL.md`. */
  path: string;
  /** `"blob"` for files, `"tree"` for directories, `"commit"` for submodules. */
  type: "blob" | "tree" | "commit" | string;
  /** Git object SHA used to fetch blob contents via {@link DiscoverAssetsInTreeEntriesOptions.fetchBlob}. */
  sha: string;
  /** Optional file mode (`"100644"` etc.). When `"100755"`, indicates an executable. */
  mode?: string;
  /** Optional blob size in bytes. */
  size?: number;
}

/**
 * Options for {@link discoverAssetsInTreeEntries}.
 *
 * @docLink packages/discovery/concepts#discover-assets-in-tree-entries-options
 */
export interface DiscoverAssetsInTreeEntriesOptions {
  /**
   * Async resolver that returns the bytes of a blob given its Git SHA.
   *
   * The store backend wires this to Octokit's
   * `repos.getContent` / `git.getBlob` API; tests can pass a synchronous
   * map-backed implementation. Returning `null` is treated identically to a
   * fetch error (the file is skipped during hashing).
   */
  fetchBlob: (sha: string) => Promise<Buffer | Uint8Array | string | null>;

  /** Optional source config (adapted from `skaile.manifest.yaml`). */
  sourceConfig?: SourceConfig;

  /**
   * Optional sidecar overlay config (loaded out-of-band from
   * `~/.skaile/sources/<slug>/skaile.manifest.yaml`). When provided, the
   * `sourceConfig` resolved from the tree (or passed explicitly) is merged
   * with this overlay via {@link mergeSourceConfigs} before dispatch.
   *
   * @docLink packages/discovery/concepts#sidecar-overlay
   */
  sidecarSourceConfig?: SourceConfig;

  /**
   * Explicit `DOMAIN.md` descriptors (repo-relative path + raw content),
   * fetched out-of-band from a sidecar repo. When provided, these are the
   * authoritative domain source and the in-tree `DOMAIN.md` scan is skipped.
   * Paths resolve against the upstream tree.
   *
   * @docLink packages/discovery/concepts#domains
   */
  domainFiles?: Array<{ path: string; content: string }>;

  /** Optional kind registry. Defaults to {@link createDefaultRegistry}. */
  registry?: IAssetKindRegistry;

  /**
   * Atomization policy. Defaults to {@link DEFAULT_ATOMIZATION_POLICY}.
   * `closure` / `full-dir` are honored on the virtual tree at full parity with
   * the local-FS path (text blobs are fetched on demand to resolve references).
   *
   * @docLink packages/discovery/concepts#atomization
   */
  atomization?: AtomizationPolicy;

  /**
   * Include assets under the source config's `dev_paths` prefixes. Defaults to
   * `false`. Mirrors the local-FS {@link discoverAssetsInTree} option.
   *
   * @docLink packages/discovery/concepts#dev-paths
   */
  includeDev?: boolean;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Discover assets in a virtual tree (Octokit `TreeEntry[]`).
 *
 * Mirrors {@link discoverAssetsInTree} but operates on a remote tree
 * snapshot — no `node:fs` access. Manifest content and per-asset file
 * bytes are fetched on demand via `options.fetchBlob`.
 *
 * @param entries - Flat list of tree entries (paths + blob SHAs). Tree
 *   entries with `type: "tree"` are ignored (directories are reconstructed
 *   from blob paths).
 * @param options - Blob fetcher + optional source config and registry.
 * @returns A {@link DiscoveryResult} identical in shape to
 *   {@link discoverAssetsInTree}'s output, including `assets`, `graph`,
 *   and `errors`.
 * @docLink packages/discovery/concepts#discover-assets-in-tree-entries
 */
export async function discoverAssetsInTreeEntries(
  entries: TreeEntry[],
  options: DiscoverAssetsInTreeEntriesOptions,
): Promise<DiscoveryResult> {
  const registry = options.registry ?? createDefaultRegistry();
  const includeDev = options.includeDev ?? false;
  const atomization = options.atomization ?? DEFAULT_ATOMIZATION_POLICY;

  // Index every blob path for O(1) lookup during file-filter construction.
  const blobByPath = new Map<string, TreeEntry>();
  for (const entry of entries) {
    if (entry.type === "blob") {
      blobByPath.set(entry.path, entry);
    }
  }

  // Author-shipped inventory dispatch.
  // If a source config was explicitly supplied with `assets:` populated, use
  // it. Otherwise, look for `skaile.manifest.yaml` in the tree itself and
  // adapt it on the fly — this mirrors how a server-side ingestion pipeline
  // would honor an author-shipped manifest without any out-of-band hint.
  const baseSourceConfig = await resolveSourceConfigFromTree(
    options.sourceConfig,
    blobByPath,
    options.fetchBlob,
  );
  // Sidecar overlay: when supplied, merge on top of the base resolved from
  // the tree. The base may be undefined (no upstream config); the sidecar
  // alone is sufficient.
  const resolvedSourceConfig = options.sidecarSourceConfig
    ? mergeSourceConfigs(baseSourceConfig, options.sidecarSourceConfig)
    : baseSourceConfig;

  // Domain descriptors: explicit `domainFiles` win; otherwise scan the tree for
  // in-repo `DOMAIN.md`. Paths resolve against the upstream tree.
  const domainMds = options.domainFiles
    ? options.domainFiles.map((f) => parseDomainMd(f.path, parseDomainFrontmatter(f.content)))
    : await collectDomainMdsFromTree(blobByPath, options.fetchBlob);

  if (resolvedSourceConfig && isManifestMode(resolvedSourceConfig)) {
    return finalizeDomains(
      await discoverFromManifestTree(
        resolvedSourceConfig,
        blobByPath,
        options.fetchBlob,
        registry,
        includeDev,
      ),
      domainMds,
    );
  }
  const devPaths = resolvedSourceConfig?.dev_paths ?? [];

  // Stable iteration order — same convention as the directory walker:
  // sorted alphabetically so dedup picks the first match (e.g. AGENT.md
  // before agent.yaml).
  const sortedBlobPaths = [...blobByPath.keys()].sort();

  const assets: DiscoveredAsset[] = [];
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];
  const allEdges: RequiresEdge[] = [];

  // Dedup key: directory + kind (matches discoverAssetsInTree).
  const seen = new Set<string>();

  for (const relativePath of sortedBlobPaths) {
    if (shouldSkipBlobPath(relativePath, includeDev, devPaths)) continue;

    const kind = resolveKindForPath(relativePath, registry, errors);
    if (!kind) continue;

    const dir = posixDirname(relativePath);
    const dedupKey = `${dir}:${kind}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const result = await buildAssetFromBlob(
      relativePath,
      kind,
      blobByPath,
      options.fetchBlob,
      resolvedSourceConfig,
      registry,
      atomization,
      errors,
      warnings,
    );
    if (!result) continue;

    allEdges.push(...result.requires);
    assets.push(result);
  }

  // Body-link dependency post-pass — parity with discoverAssetsInTree.
  allEdges.push(...(await extractBodyLinkEdgesTree(assets, blobByPath, options.fetchBlob)));

  const graph = buildRequiresGraph(allEdges);
  return finalizeDomains({ assets, graph, errors, warnings }, domainMds);
}

// ---------------------------------------------------------------------------
// Domains (virtual tree)
// ---------------------------------------------------------------------------

/** Parse a `DOMAIN.md`'s YAML frontmatter into a plain object. */
function parseDomainFrontmatter(content: string): Record<string, unknown> {
  const { data } = parseFrontmatter(content);
  return data && typeof data === "object" ? (data as Record<string, unknown>) : {};
}

/** Fetch and parse every in-tree `DOMAIN.md` descriptor. */
async function collectDomainMdsFromTree(
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<DomainMd[]> {
  const out: DomainMd[] = [];
  for (const [p, entry] of blobByPath) {
    if (!isDomainMdPath(p)) continue;
    let bytes: Buffer | null;
    try {
      bytes = await fetchAsBuffer(fetchBlob, entry.sha);
    } catch {
      bytes = null;
    }
    if (bytes) out.push(parseDomainMd(p, parseDomainFrontmatter(bytes.toString("utf-8"))));
  }
  return out;
}

/** Stamp `domainSlug` on assets and attach the domain index to a result. */
function finalizeDomains(result: DiscoveryResult, domainMds: DomainMd[]): DiscoveryResult {
  if (domainMds.length === 0) return result;
  const { domains, errors } = applyDomains(result.assets, domainMds);
  result.errors.push(...errors);
  if (domains.length > 0) result.domains = domains;
  return result;
}

/**
 * Whether a blob path should be skipped before kind resolution: the well-known
 * VCS/dependency directories (mirrors the local walker) and dev-only paths
 * (excluded from a normal run; `includeDev` re-includes them).
 */
function shouldSkipBlobPath(
  relativePath: string,
  includeDev: boolean,
  devPaths: string[],
): boolean {
  if (
    relativePath.startsWith(".git/") ||
    relativePath.startsWith("node_modules/") ||
    relativePath.startsWith(".skaile/") ||
    relativePath.includes("/.git/") ||
    relativePath.includes("/node_modules/") ||
    relativePath.includes("/.skaile/")
  ) {
    return true;
  }
  if (!includeDev && isExcludedDevPath(relativePath, devPaths)) return true;
  return false;
}

/**
 * Resolve the asset kind for a path. Returns `undefined` when no kind matches;
 * a thrown resolver error is recorded in `errors` and also yields `undefined`.
 */
function resolveKindForPath(
  relativePath: string,
  registry: IAssetKindRegistry,
  errors: Array<{ path: string; error: string }>,
): string | undefined {
  try {
    return registry.resolveKind(relativePath);
  } catch (err) {
    errors.push({
      path: relativePath,
      error: err instanceof Error ? err.message : String(err),
    });
    return undefined;
  }
}

/**
 * Fetch + parse the manifest blob, derive identity, compute the file filter and
 * hash for a single kind-matched blob path. Returns the {@link DiscoveredAsset}
 * or `null` when any step fails (the failure is appended to `errors`).
 */
async function buildAssetFromBlob(
  relativePath: string,
  kind: string,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  resolvedSourceConfig: SourceConfig | undefined,
  registry: IAssetKindRegistry,
  atomization: AtomizationPolicy,
  errors: Array<{ path: string; error: string }>,
  warnings: ReferenceWarning[],
): Promise<DiscoveredAsset | null> {
  const manifestEntry = blobByPath.get(relativePath);
  if (!manifestEntry) {
    // Should never happen — `relativePath` came from `blobByPath`.
    errors.push({ path: relativePath, error: "Manifest blob disappeared from tree index" });
    return null;
  }

  let manifestBytes: Buffer | null;
  try {
    manifestBytes = await fetchAsBuffer(fetchBlob, manifestEntry.sha);
  } catch (err) {
    errors.push({
      path: relativePath,
      error: `Failed to fetch manifest blob: ${err instanceof Error ? err.message : String(err)}`,
    });
    return null;
  }
  if (!manifestBytes) {
    errors.push({ path: relativePath, error: "Failed to fetch manifest blob" });
    return null;
  }

  const manifestText = manifestBytes.toString("utf-8");
  const manifest = parseManifestText(relativePath, manifestText);
  if (!manifest) {
    errors.push({ path: relativePath, error: "Failed to parse manifest" });
    return null;
  }

  const publisher = resolvePublisher(relativePath, resolvedSourceConfig);
  const name = deriveName(relativePath, kind, manifest);
  const version = coerceVersion(manifest);
  // `publisher` is bare; the canonical ref carries the `@` scope sigil.
  const ref = `@${publisher}/${name}@${version}`;

  // File filter — kind-aware, computed against the virtual tree.
  const fileFilter = computeFileFilter(kind, relativePath, blobByPath);

  // Apply the atomization policy on top of the structural filter (parity with
  // discover.ts). May expand fileFilter.files; appends warnings.
  warnings.push(
    ...(await applyAtomizationPolicyTree(
      atomization,
      relativePath,
      fileFilter,
      blobByPath,
      fetchBlob,
      registry,
    )),
  );

  // SHA256 — hash deterministically over the curated file set, fetching
  // each blob's bytes on demand.
  let sha256: string;
  try {
    sha256 = await computeDeterministicHashFromTree(fileFilter, blobByPath, fetchBlob);
  } catch (err) {
    errors.push({
      path: relativePath,
      error: `Failed to compute hash: ${err instanceof Error ? err.message : String(err)}`,
    });
    return null;
  }

  const requires = registry.extractRequires(kind, ref, manifest);

  return {
    ref,
    kind,
    publisher,
    name,
    version,
    manifest,
    // The tree-entries variant has no on-disk path — surface the
    // repo-relative path here so downstream consumers can build a
    // Manifest URL (raw.githubusercontent.com link).
    manifestPath: relativePath,
    relativePath,
    fileFilter,
    sha256,
    requires,
  };
}

// ---------------------------------------------------------------------------
// Manifest parsing (string-driven; mirrors discover.ts)
// ---------------------------------------------------------------------------

function parseManifestText(relativePath: string, text: string): Record<string, unknown> | null {
  try {
    if (relativePath.endsWith(".md")) {
      const { data } = parseFrontmatter(text);
      // A .md with no `---` block yields `{}`; for a kind-matched file that is a
      // parse failure, not a default-identity manifest.
      if (!data || Object.keys(data).length === 0) return null;
      return data as Record<string, unknown>;
    }
    if (relativePath.endsWith(".yaml") || relativePath.endsWith(".yml")) {
      const parsed = yaml.load(text);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
      return null;
    }
    if (relativePath.endsWith(".json")) {
      return JSON.parse(text) as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Publisher resolution (copy of discover.ts to avoid exporting an internal)
// ---------------------------------------------------------------------------

function resolvePublisher(relativePath: string, sourceConfig?: SourceConfig): string {
  if (!sourceConfig) {
    const domain = relativePath.split("/")[0] ?? "unknown";
    return domain;
  }
  for (const override of sourceConfig.publisher_overrides) {
    if (relativePath.startsWith(override.path)) {
      return barePublisher(override.publisher);
    }
  }
  const template = sourceConfig.publisher_default;
  if (template.includes("<domain>")) {
    const domain = relativePath.split("/")[0] ?? "unknown";
    return barePublisher(template.replace("<domain>", domain));
  }
  return barePublisher(template);
}

// ---------------------------------------------------------------------------
// Name derivation (mirrors discover.ts)
// ---------------------------------------------------------------------------

/**
 * Resolve an asset's version from its manifest (mirrors discover.ts). Prefers
 * the top-level `version:` field, then falls back to `metadata.version` —
 * Claude-skill `SKILL.md` frontmatter conventionally nests custom fields under
 * `metadata:`. Unquoted YAML (`version: 1.0`) parses as a JS number, so scalars
 * are coerced to strings. Defaults to "0.1.0" (not "0.0.0", which the Catalog
 * rejects) when absent or non-scalar.
 */
function coerceVersion(manifest: Record<string, unknown>): string {
  const scalar = (v: unknown): string | null =>
    typeof v === "string" ? v : typeof v === "number" ? String(v) : null;
  const top = scalar(manifest.version);
  if (top !== null) return top;
  const metadata = manifest.metadata;
  if (metadata !== null && typeof metadata === "object") {
    const nested = scalar((metadata as Record<string, unknown>).version);
    if (nested !== null) return nested;
  }
  return "0.1.0";
}

function deriveName(relativePath: string, kind: string, manifest: Record<string, unknown>): string {
  // Accept manifest.name only if canonical — an untrusted name with `/`, `@`,
  // or `..` would flow into refs/paths.
  if (
    typeof manifest.name === "string" &&
    manifest.name.length > 0 &&
    isValidAssetName(manifest.name)
  ) {
    return manifest.name;
  }
  const parts = relativePath.split("/");
  if (kind === "flow") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.flow\.(yaml|json)$/, "");
  }
  if (kind === "prompt") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.prompt\.md$/, "");
  }
  if (kind === "preset") {
    const filename = parts[parts.length - 1] ?? "";
    return filename.replace(/\.preset\.yaml$/, "");
  }
  if (parts.length >= 2) {
    return parts[parts.length - 2]!;
  }
  const filename = parts[parts.length - 1] ?? "unknown";
  return filename.replace(/\.(md|yaml|json)$/, "");
}

// ---------------------------------------------------------------------------
// File-filter computation against a virtual tree
// ---------------------------------------------------------------------------

/**
 * Compute the per-kind default file filter against a virtual tree.
 *
 * Mirrors the per-kind logic in `builtin-providers.ts`. Kept inline because
 * the provider implementations there read `fs.readdirSync`/`fs.existsSync`,
 * which doesn't translate to a virtual `TreeEntry[]`. For unknown kinds
 * (extension kinds without a file filter, or kinds with `defaultFileFilter:
 * manifestOnly`) we fall back to manifest-only.
 */
function computeFileFilter(
  kind: string,
  manifestPath: string,
  blobByPath: Map<string, TreeEntry>,
): AssetKindFileFilter {
  const dir = posixDirname(manifestPath);
  const manifestName = posixBasename(manifestPath);

  switch (kind) {
    case "skill":
      return skillFilterVirtual(dir, manifestName, blobByPath);
    case "agent":
      return agentFilterVirtual(dir, manifestName, blobByPath);
    case "contract":
      return contractFilterVirtual(dir, manifestName, blobByPath);
    case "connector":
      return connectorFilterVirtual(dir, manifestName, blobByPath);
    // mcp-server, prompt, preset, persona, ruleset, knowledge: manifest-only
    default:
      return { assetRoot: dir, files: [manifestName] };
  }
}

/**
 * skill: SKILL.md + same-dir leaves (validator.py, CLI.md)
 *   + convention companion subdirs (SKILL_COMPANION_DIRS) + nested sub-skills.
 * Mirrors `skillFilter` in builtin-providers.ts for exact store/local parity.
 */
function skillFilterVirtual(
  dir: string,
  manifestName: string,
  blobByPath: Map<string, TreeEntry>,
): AssetKindFileFilter {
  const files = new Set<string>([manifestName]);

  // Same-dir leaves.
  for (const leaf of ["validator.py", "CLI.md"]) {
    if (blobByPath.has(joinPosix(dir, leaf))) files.add(leaf);
  }

  // Direct child directories of `dir`.
  const directChildDirs = new Set<string>();
  for (const blobPath of blobByPath.keys()) {
    if (!isInDir(blobPath, dir)) continue;
    const remainder = blobPath.slice(dir === "" ? 0 : dir.length + 1);
    const slash = remainder.indexOf("/");
    if (slash === -1) continue; // direct file already handled
    directChildDirs.add(remainder.slice(0, slash));
  }

  // Convention companion subdirs — included entirely.
  const companions = new Set(SKILL_COMPANION_DIRS);
  for (const sub of SKILL_COMPANION_DIRS) {
    if (directChildDirs.has(sub)) addAllUnder(files, dir, sub, blobByPath);
  }

  // Nested SKILL dirs (not already a companion) — included entirely.
  for (const childDir of directChildDirs) {
    if (companions.has(childDir)) continue;
    if (blobByPath.has(joinPosix(dir, childDir, "SKILL.md"))) {
      addAllUnder(files, dir, childDir, blobByPath);
    }
  }

  return { assetRoot: dir, files: [...files].sort() };
}

/**
 * agent: agent.yaml or AGENT.md + same-dir SOUL.md, RULES.md
 *   + convention companion subdirs (AGENT_COMPANION_DIRS).
 * Mirrors `agentFilter` in builtin-providers.ts for exact store/local parity.
 */
function agentFilterVirtual(
  dir: string,
  manifestName: string,
  blobByPath: Map<string, TreeEntry>,
): AssetKindFileFilter {
  const files = new Set<string>([manifestName]);
  for (const companion of ["SOUL.md", "RULES.md"]) {
    if (blobByPath.has(joinPosix(dir, companion))) files.add(companion);
  }
  for (const sub of AGENT_COMPANION_DIRS) {
    addAllUnder(files, dir, sub, blobByPath);
  }
  return { assetRoot: dir, files: [...files].sort() };
}

/**
 * contract: CONTRACT.md + same-dir attachments (all non-directory entries).
 */
function contractFilterVirtual(
  dir: string,
  manifestName: string,
  blobByPath: Map<string, TreeEntry>,
): AssetKindFileFilter {
  const files = new Set<string>([manifestName]);
  for (const blobPath of blobByPath.keys()) {
    if (!isInDir(blobPath, dir)) continue;
    const remainder = blobPath.slice(dir === "" ? 0 : dir.length + 1);
    if (remainder.includes("/")) continue; // not direct child
    files.add(remainder);
  }
  return { assetRoot: dir, files: [...files].sort() };
}

/**
 * connector: CONNECTOR.md + entry file (adapter.ts by default).
 */
function connectorFilterVirtual(
  dir: string,
  manifestName: string,
  blobByPath: Map<string, TreeEntry>,
): AssetKindFileFilter {
  const files = new Set<string>([manifestName]);
  if (blobByPath.has(joinPosix(dir, "adapter.ts"))) files.add("adapter.ts");
  return { assetRoot: dir, files: [...files].sort() };
}

// ---------------------------------------------------------------------------
// Deterministic hashing over a virtual tree
// ---------------------------------------------------------------------------

/**
 * Compute a deterministic SHA256 hash over a virtual file set, fetching each
 * blob's bytes via `fetchBlob`. Mirrors the hashing convention in
 * {@link computeDeterministicHash}: files sorted, path + null + content +
 * null per file. Missing or unfetchable files are skipped (non-fatal,
 * matching the local case where optional files like `validator.py` may not
 * exist).
 */
async function computeDeterministicHashFromTree(
  fileFilter: AssetKindFileFilter,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<string> {
  const hash = crypto.createHash("sha256");
  const sorted = [...fileFilter.files].sort();
  for (const file of sorted) {
    const fullPath = joinPosix(fileFilter.assetRoot, file);
    const entry = blobByPath.get(fullPath);
    if (!entry) continue; // file referenced by filter but absent in tree
    hash.update(file);
    hash.update("\0");
    let bytes: Buffer | null;
    try {
      bytes = await fetchAsBuffer(fetchBlob, entry.sha);
    } catch {
      bytes = null;
    }
    if (bytes) hash.update(bytes);
    hash.update("\0");
  }
  return hash.digest("hex");
}

// ---------------------------------------------------------------------------
// Atomization over a virtual tree (parity with discover.ts)
// ---------------------------------------------------------------------------

// Text extensions the reference scan reads. Mirrors SCANNABLE_EXT in
// reference-lint.ts (kept local to avoid exporting an internal constant).
const TREE_SCANNABLE_EXT = new Set([".md", ".mdx", ".markdown", ".txt"]);

/**
 * Pre-fetch the text of every scannable blob under `assetRoot` into a map, so
 * the synchronous {@link AssetFs} scan can read content without async calls.
 */
async function prefetchAssetText(
  assetRoot: string,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<Map<string, string>> {
  const prefix = assetRoot === "" ? "" : `${assetRoot}/`;
  const out = new Map<string, string>();
  for (const [full, entry] of blobByPath) {
    if (assetRoot !== "" && !full.startsWith(prefix)) continue;
    if (!TREE_SCANNABLE_EXT.has(path.posix.extname(full).toLowerCase())) continue;
    let bytes: Buffer | null;
    try {
      bytes = await fetchAsBuffer(fetchBlob, entry.sha);
    } catch {
      bytes = null;
    }
    if (bytes) out.set(full, bytes.toString("utf-8"));
  }
  return out;
}

/** An {@link AssetFs} backed by the virtual tree's blob index + pre-fetched text. */
function createTreeAssetFs(
  assetRoot: string,
  blobByPath: Map<string, TreeEntry>,
  textByPath: Map<string, string>,
): AssetFs {
  const full = (rel: string) => joinPosix(assetRoot, rel);
  return {
    readText(rel) {
      return textByPath.get(full(rel)) ?? null;
    },
    statKind(rel) {
      const f = full(rel);
      if (blobByPath.has(f)) return "file";
      const dirPrefix = `${f}/`;
      for (const k of blobByPath.keys()) if (k.startsWith(dirPrefix)) return "dir";
      return "absent";
    },
    listFilesUnder(rel) {
      const f = full(rel);
      if (blobByPath.has(f)) return [rel];
      const dirPrefix = `${f}/`;
      const out: string[] = [];
      for (const k of blobByPath.keys()) {
        if (k.startsWith(dirPrefix)) out.push(assetRoot === "" ? k : k.slice(assetRoot.length + 1));
      }
      return out;
    },
  };
}

/**
 * Whole-directory capture over a virtual tree, carving out nested sub-asset
 * subtrees. Mirrors `collectFullDirFiles` in discover.ts.
 */
function collectFullDirFilesTree(
  assetRoot: string,
  blobByPath: Map<string, TreeEntry>,
  registry: IAssetKindRegistry,
): string[] {
  const prefix = assetRoot === "" ? "" : `${assetRoot}/`;
  const all: string[] = [];
  const nestedDirs: string[] = [];
  for (const full of blobByPath.keys()) {
    if (assetRoot !== "" && !full.startsWith(prefix)) continue;
    const rel = assetRoot === "" ? full : full.slice(assetRoot.length + 1);
    all.push(rel);
    if (rel.includes("/")) {
      let kind: string | undefined;
      try {
        kind = registry.resolveKind(rel);
      } catch {
        kind = undefined;
      }
      if (kind) nestedDirs.push(posixDirname(rel));
    }
  }
  const carved = [...new Set(nestedDirs)];
  return all.filter((rel) => !carved.some((d) => rel.startsWith(`${d}/`))).sort();
}

/**
 * Apply the atomization policy to a virtual-tree file filter in place, fetching
 * the asset's text blobs as needed. Returns the warnings produced.
 */
async function applyAtomizationPolicyTree(
  policy: AtomizationPolicy,
  relativePath: string,
  fileFilter: AssetKindFileFilter,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  registry: IAssetKindRegistry,
): Promise<ReferenceWarning[]> {
  const textByPath = await prefetchAssetText(fileFilter.assetRoot, blobByPath, fetchBlob);
  const afs = createTreeAssetFs(fileFilter.assetRoot, blobByPath, textByPath);

  if (policy === "strict") {
    return lintUncoveredReferences(relativePath, afs, fileFilter.files);
  }

  const structural = new Set(fileFilter.files);
  fileFilter.files =
    policy === "full-dir"
      ? collectFullDirFilesTree(fileFilter.assetRoot, blobByPath, registry)
      : closeOverReferences(afs, fileFilter.files).files;

  const reason =
    policy === "full-dir" ? "full-dir capture" : "reference closure (referenced but uncaptured)";
  return fileFilter.files
    .filter((f) => !structural.has(f))
    .map((f) => ({ path: relativePath, warning: `captured "${f}" via ${reason}` }));
}

/**
 * Body-link dependency post-pass over a virtual tree. Mirrors
 * `extractBodyLinkEdges` in discover.ts: both a path-based matcher (a reference
 * escaping its asset root onto another asset) and a name-based matcher (a
 * standalone `/<name>` slash command matching exactly one other asset) yield
 * soft `body-link` edges. Kept in strict parity with the local FS path.
 */
async function extractBodyLinkEdgesTree(
  assets: DiscoveredAsset[],
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<RequiresEdge[]> {
  const roots = assets
    .map((a) => ({ root: a.fileFilter.assetRoot, ref: a.ref }))
    .sort((x, y) => y.root.length - x.root.length);
  const nameToRefs = buildSkillNameToRefs(assets);

  const edges: RequiresEdge[] = [];
  const seen = new Set<string>();

  for (const asset of assets) {
    const root = asset.fileFilter.assetRoot;
    const textByPath = await prefetchAssetText(root, blobByPath, fetchBlob);
    const afs = createTreeAssetFs(root, blobByPath, textByPath);
    for (const r of scanAssetReferences(afs, asset.fileFilter.files)) {
      if (r.classification !== "external") continue;
      const target = path.posix.normalize(joinPosix(root, r.resolved));
      const hit = roots.find(
        (cand) =>
          cand.ref !== asset.ref && (target === cand.root || target.startsWith(`${cand.root}/`)),
      );
      if (hit) pushBodyLinkEdge(edges, seen, asset.ref, hit.ref);
    }
    for (const name of scanSlashCommandNames(afs, asset.fileFilter.files)) {
      const to = resolveSlashCommand(nameToRefs, name, asset.ref);
      if (to) pushBodyLinkEdge(edges, seen, asset.ref, to);
    }
  }

  return edges;
}

// ---------------------------------------------------------------------------
// POSIX path helpers (TreeEntry paths always use forward slashes)
// ---------------------------------------------------------------------------

function posixDirname(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? "" : p.slice(0, i);
}

function posixBasename(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? p : p.slice(i + 1);
}

function joinPosix(...segments: string[]): string {
  return segments.filter((s) => s !== "").join("/");
}

function isInDir(blobPath: string, dir: string): boolean {
  if (dir === "") return true;
  return blobPath.startsWith(`${dir}/`);
}

function addAllUnder(
  out: Set<string>,
  baseDir: string,
  subDir: string,
  blobByPath: Map<string, TreeEntry>,
): void {
  const prefix = baseDir === "" ? `${subDir}/` : `${baseDir}/${subDir}/`;
  for (const blobPath of blobByPath.keys()) {
    if (blobPath.startsWith(prefix)) {
      // Strip baseDir prefix to get path relative to assetRoot.
      const relToAssetRoot = baseDir === "" ? blobPath : blobPath.slice(baseDir.length + 1);
      out.add(relToAssetRoot);
    } else if (blobPath === prefix.slice(0, -1)) {
      // exact match (a file named exactly subDir, unlikely but defensive)
      out.add(subDir);
    }
  }
}

// ---------------------------------------------------------------------------
// Blob fetcher normalisation
// ---------------------------------------------------------------------------

async function fetchAsBuffer(
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  sha: string,
): Promise<Buffer | null> {
  const result = await fetchBlob(sha);
  if (result == null) return null;
  if (Buffer.isBuffer(result)) return result;
  if (typeof result === "string") return Buffer.from(result, "utf-8");
  // Uint8Array
  return Buffer.from(result);
}

// ---------------------------------------------------------------------------
// Author-shipped manifest dispatch (virtual tree)
// ---------------------------------------------------------------------------

/**
 * Resolve the source config to use for this discovery run.
 *
 * 1. If the caller passed `options.sourceConfig`, use it as-is.
 * 2. Otherwise, look up `skaile.manifest.yaml` in the tree and adapt it. This
 *    lets a server-side ingestion pipeline honor an author-shipped inventory
 *    without any out-of-band hint.
 */
async function resolveSourceConfigFromTree(
  explicit: SourceConfig | undefined,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<SourceConfig | undefined> {
  if (explicit) return explicit;
  const entry = blobByPath.get(SKAILE_MANIFEST_FILENAME);
  if (!entry) return undefined;
  const bytes = await fetchAsBuffer(fetchBlob, entry.sha);
  if (!bytes) return undefined;
  const result = decodeManifestSourceConfig(bytes.toString("utf-8"));
  return result.ok ? result.config : undefined;
}

/**
 * Discover assets from an author-shipped inventory against a virtual tree.
 *
 * Mirrors `discoverFromManifest()` from discover-manifest.ts but uses the
 * `TreeEntry[]` blob index + async `fetchBlob` instead of `node:fs`. Hash
 * algorithm is identical so the same content produces the same SHA256 on
 * local FS and remote tree.
 */
async function discoverFromManifestTree(
  sourceConfig: SourceConfigV2,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  registry: IAssetKindRegistry,
  includeDev = false,
): Promise<DiscoveryResult> {
  const assets: DiscoveredAsset[] = [];
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];
  const allEdges: RequiresEdge[] = [];
  const devPaths = sourceConfig.dev_paths ?? [];

  const sortedEntries = [...sourceConfig.assets].sort((a, b) => (a.path < b.path ? -1 : 1));

  for (const entry of sortedEntries) {
    // Dev-only assets are excluded from a normal run; --dev re-includes them.
    if (!includeDev && isExcludedDevPath(entry.path, devPaths)) continue;
    const result = await processManifestEntryTree(entry, blobByPath, fetchBlob, registry);
    warnings.push(...result.warnings);
    if (result.errors.length > 0) {
      errors.push(...result.errors);
      if (!result.asset) continue;
    }
    if (result.asset) {
      assets.push(result.asset);
      allEdges.push(...result.asset.requires);
    }
  }

  const graph = buildRequiresGraph(allEdges);
  return { assets, graph, errors, warnings };
}

interface ProcessEntryTreeResult {
  asset: DiscoveredAsset | null;
  errors: Array<{ path: string; error: string }>;
  warnings: ReferenceWarning[];
}

async function processManifestEntryTree(
  entry: AssetEntry,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  registry: IAssetKindRegistry,
): Promise<ProcessEntryTreeResult> {
  const errors: Array<{ path: string; error: string }> = [];
  const warnings: ReferenceWarning[] = [];
  const assetRootRelative = entry.path;

  const files = resolveEntryFilesTree(entry, blobByPath, errors);
  if (!files) return { asset: null, errors, warnings };

  // Pick the manifest file (provider's matchesPath wins; filename heuristic fallback).
  const manifestFileRel = pickManifestFileTree(entry.path, files, entry.kind, registry);
  const manifestFull = joinPosix(entry.path, manifestFileRel);

  const manifest = await fetchAndParseManifestTree(
    manifestFull,
    manifestFileRel,
    assetRootRelative,
    blobByPath,
    fetchBlob,
    errors,
  );
  if (!manifest) return { asset: null, errors, warnings };

  // Ghost-install on unknown kind.
  if (!registry.getProvider(entry.kind)) {
    warnings.push({
      path: assetRootRelative,
      warning: `Unknown asset kind "${entry.kind}" — ghost-installing without provider validation`,
    });
  }

  // Requires syntax.
  const requiresErrors = validateRequiresSyntax(entry.requires);
  if (requiresErrors.length > 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestRequiresError: ${requiresErrors.join("; ")}`,
    });
    return { asset: null, errors, warnings };
  }

  const computedHash = await computeAndVerifyHashTree(
    entry,
    files,
    assetRootRelative,
    blobByPath,
    fetchBlob,
    errors,
    warnings,
  );
  if (computedHash === null) return { asset: null, errors, warnings };

  // Bare publisher (core/src/ref.ts invariant); the ref keeps one `@` sigil,
  // matching the glob + local-manifest paths.
  const publisher = barePublisher(entry.publisher);
  const ref = `@${publisher}/${entry.name}@${entry.version}`;
  const requires: RequiresEdge[] = entry.requires.map((to) => ({
    from: ref,
    to,
    field: "assets[].requires",
  }));

  const mergedManifest: Record<string, unknown> = {
    ...(entry.metadata ?? {}),
    ...manifest,
  };

  const asset: DiscoveredAsset = {
    ref,
    kind: entry.kind,
    publisher,
    name: entry.name,
    version: entry.version,
    manifest: mergedManifest,
    manifestPath: manifestFull,
    relativePath: manifestFull,
    fileFilter: {
      assetRoot: entry.path,
      files: [...files].sort(),
    },
    sha256: computedHash,
    requires,
  };

  return { asset, errors, warnings };
}

/**
 * Resolve the entry's file list and verify every file is present in the tree.
 * A `root`-only entry (no explicit `files`) is materialised by filtering the
 * tree under `entry.path` — mirrors the local FS recursive walk. Returns the
 * file list, or `null` (with an error appended) when the root is empty or any
 * declared file is missing.
 */
function resolveEntryFilesTree(
  entry: AssetEntry,
  blobByPath: Map<string, TreeEntry>,
  errors: Array<{ path: string; error: string }>,
): string[] | null {
  const assetRootRelative = entry.path;
  const files = entry.files ?? expandRootFilesTree(entry.path, blobByPath);
  if (entry.files === undefined && files.length === 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestFileMissing: root "${entry.root ?? entry.path}" contains no files`,
    });
    return null;
  }

  const missing: string[] = [];
  for (const file of files) {
    const full = joinPosix(entry.path, file);
    if (!blobByPath.has(full)) missing.push(file);
  }
  if (missing.length > 0) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestFileMissing: ${missing.join(", ")}`,
    });
    return null;
  }
  return files;
}

/**
 * Fetch the manifest blob and parse it. Returns the parsed manifest, or `null`
 * (with an error appended) on fetch or parse failure.
 */
async function fetchAndParseManifestTree(
  manifestFull: string,
  manifestFileRel: string,
  assetRootRelative: string,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  errors: Array<{ path: string; error: string }>,
): Promise<Record<string, unknown> | null> {
  const manifestEntry = blobByPath.get(manifestFull)!;
  let manifestBytes: Buffer | null;
  try {
    manifestBytes = await fetchAsBuffer(fetchBlob, manifestEntry.sha);
  } catch (err) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestSchemaError: failed to fetch manifest blob: ${err instanceof Error ? err.message : String(err)}`,
    });
    return null;
  }
  if (!manifestBytes) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestSchemaError: failed to fetch manifest blob`,
    });
    return null;
  }
  const manifest = parseManifestText(manifestFull, manifestBytes.toString("utf-8"));
  if (!manifest) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestSchemaError: failed to parse manifest file ${manifestFileRel}`,
    });
    return null;
  }
  return manifest;
}

/**
 * Compute the deterministic hash over the curated file set and apply the
 * author-pinned gate. Returns the computed hash, or `null` when hashing fails
 * or a declared `sha256` mismatches (both append an error). A missing `sha256`
 * is a warning (appended) but still returns the computed hash.
 */
async function computeAndVerifyHashTree(
  entry: AssetEntry,
  files: string[],
  assetRootRelative: string,
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
  errors: Array<{ path: string; error: string }>,
  warnings: ReferenceWarning[],
): Promise<string | null> {
  let computedHash: string;
  try {
    computedHash = await computeManifestHashTree(entry.path, files, blobByPath, fetchBlob);
  } catch (err) {
    errors.push({
      path: assetRootRelative,
      error: `Failed to compute hash: ${err instanceof Error ? err.message : String(err)}`,
    });
    return null;
  }

  if (entry.sha256 && entry.sha256 !== computedHash) {
    errors.push({
      path: assetRootRelative,
      error: `ManifestHashMismatch: declared ${entry.sha256}, computed ${computedHash}`,
    });
    return null;
  }

  if (!entry.sha256) {
    // Coded so the sync layer can collapse many of these into one summary line;
    // per-asset computed hash omitted (it added noise, one line per asset).
    warnings.push({
      path: assetRootRelative,
      warning: "Missing sha256 — auto-computed locally (only needed for store publish).",
      code: "missing_sha256",
    });
  }

  return computedHash;
}

const MANIFEST_FILENAMES = new Set([
  "SKILL.md",
  "AGENT.md",
  "agent.yaml",
  "CONNECTOR.md",
  "MCP.md",
  "CONTRACT.md",
  "PERSONA.md",
  "RULESET.md",
]);

const MANIFEST_SUFFIXES = [".flow.yaml", ".flow.json", ".prompt.md", ".preset.yaml"];

function pickManifestFileTree(
  assetPath: string,
  files: string[],
  kind: string,
  registry: IAssetKindRegistry,
): string {
  const provider = registry.getProvider(kind);
  if (provider) {
    for (const file of files) {
      if (provider.matchesPath(joinPosix(assetPath, file))) return file;
    }
  }
  for (const file of files) {
    const base = file.split("/").pop() ?? file;
    if (MANIFEST_FILENAMES.has(base)) return file;
    if (MANIFEST_SUFFIXES.some((s) => file.endsWith(s))) return file;
  }
  return files[0]!;
}

/**
 * List every blob under `assetPath` in the virtual tree, relative to it,
 * skipping `.git` / `node_modules` / `.skaile`. The tree counterpart to
 * `expandRootFiles` in discover-manifest.ts.
 */
function expandRootFilesTree(assetPath: string, blobByPath: Map<string, TreeEntry>): string[] {
  const out: string[] = [];
  const root = assetPath === "." ? "" : assetPath;
  for (const blobPath of blobByPath.keys()) {
    if (!isInDir(blobPath, root)) continue;
    const rel = root === "" ? blobPath : blobPath.slice(root.length + 1);
    if (
      rel.startsWith(".git/") ||
      rel.startsWith("node_modules/") ||
      rel.startsWith(".skaile/") ||
      rel.includes("/.git/") ||
      rel.includes("/node_modules/") ||
      rel.includes("/.skaile/")
    ) {
      continue;
    }
    out.push(rel);
  }
  return out.sort();
}

async function computeManifestHashTree(
  assetPath: string,
  files: string[],
  blobByPath: Map<string, TreeEntry>,
  fetchBlob: DiscoverAssetsInTreeEntriesOptions["fetchBlob"],
): Promise<string> {
  const hash = crypto.createHash("sha256");
  const sorted = [...files].sort();
  for (const file of sorted) {
    const full = joinPosix(assetPath, file);
    const blob = blobByPath.get(full);
    if (!blob) continue; // existence already checked, defensive
    hash.update(file);
    hash.update("\0");
    const bytes = await fetchAsBuffer(fetchBlob, blob.sha);
    if (bytes) hash.update(bytes);
    hash.update("\0");
  }
  return hash.digest("hex");
}

function validateRequiresSyntax(requires: string[]): string[] {
  const errors: string[] = [];
  const RE = /^@[\w-]+\/[\w.-]+(?:@.+)?$/;
  for (const r of requires) {
    if (!RE.test(r)) errors.push(`invalid requires entry "${r}"`);
  }
  return errors;
}
